import React, { useEffect, useState } from 'react';
import { PlusCircle, FileText } from 'lucide-react';
import TranslationNavBarComponent from '../../generic/TranslationNavBarComponent';
import { useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import TranslationRequestRepository from '../../../repository/TranslationRequestRepository';
import { TranslationRequest } from '../../../model/Translation';
import TranslationRequests from '../../generic/TranslationRequests';
import { Button } from '@aws-amplify/ui-react';
import { deleteTranslationRequest } from '../../../api/TranslationRequestApi';

const items = [
    { id: 1, title: 'My Requests', url: '/translations/myrequests' },
];

const TranslationsMyRequestsScreen: React.FC = () => {
    const [menuItems] = useState(items);
    const [selectedItem, setSelectedItem] = useState<number>(1);
    const [requests, setRequests] = useState<TranslationRequest[]>([]);
    const [lastPartitionKey, setLastPartitionKey] = useState('');
    const [lastRangeKey, setLastRangeKey] = useState('');
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        let isSubscribed = true;
        if (isSubscribed) {
            fetchMyRequests();
        }
        return () => {
            isSubscribed = false;
        }
    }, []);

    const handleNewTranslation = () => {
        console.log('Creating new translation');
        navigate("/translations/create");
    };

    const handleMenuItemClick = (id: number, url: string) => {
        setSelectedItem(id);
        navigate(url);
    };

    const fetchMyRequests = async () => {
        try {
            setIsLoading(true);
            let results = await TranslationRequestRepository.fetchMyRequests();
            if (results != null) {
                setRequests(results.results);
                setLastPartitionKey(results.last_partition_key ?? '');
                setLastRangeKey(results.last_range_key ?? '');
            }
        } catch (error) {

        } finally {
            setIsLoading(false);
        }
    }

    const handleLoadMoreClicked = async () => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            let results = await TranslationRequestRepository.fetchMyRequests(lastPartitionKey, lastRangeKey);
            setRequests(prev => [...prev, ...results.results])
            setLastPartitionKey(results.last_partition_key ?? '');
            setLastRangeKey(results.last_range_key ?? '');
        } catch (error) {
            alert("Could not load more");
        } finally {
            setIsLoading(false);
        }
    }

    const handlePayRequest = (rangeKey: string) => {
        console.log(`Paying for request with rangeKey: ${rangeKey}`);
        // Implement payment logic here
    };

    const handleCreateClick = () => {
        navigate("/translations/create")
    }

    const handleViewDetails = (rangeKey: string) => {
        console.log(`Viewing details for request with rangeKey: ${rangeKey}`);
        // Navigate to details screen logic here
    };

    const handleDeleteClicked = async (translation: TranslationRequest) => {
        setIsLoading(true);
        try {
            let result = await deleteTranslationRequest(translation.partition_key, translation.range_key);
            if (result) {
                setRequests((prev) =>
                    prev.map((item) =>
                        item.range_key === translation.range_key ? { ...item, status: "DELETING" } : item
                    )
                )
            }
        } catch (error) {
            alert("Unable to delete");
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <div className="d-flex flex-column min-vh-100">
            {/* Header */}
            <TranslationNavBarComponent />
            <div className="container-fluid">
                <div className="row">
                    {/* Sidebar */}
                    <div className="col-md-3 col-lg-2 d-md-block bg-light sidebar collapse">
                        <div className="position-sticky pt-3">
                            <ul className="nav flex-column">
                                {menuItems.map((translation) => (
                                    <li key={translation.id} className="nav-item">
                                        <a
                                            href="#"
                                            className={`nav-link ${selectedItem === translation.id ? 'active' : ''}`}
                                            onClick={() => handleMenuItemClick(translation.id, translation.url)}
                                            style={{
                                                backgroundColor: selectedItem === translation.id ? '#007bff' : 'transparent',
                                                color: selectedItem === translation.id ? '#ffffff' : '#000000',
                                            }}
                                        >
                                            <FileText className="me-2" size={18} />
                                            {translation.title}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Main content */}
                    <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4">
                        <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom">
                            <h1 className="h2">My Translation Requests <Button onClick={handleCreateClick}>Create</Button> {lastRangeKey.length > 0 && (<Button style={{ marginLeft: '24px' }} onClick={handleLoadMoreClicked}>Load More</Button>)}</h1>
                        </div>
                        {isLoading && (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <CircularProgress />
                            </div>
                        )}
                        {requests.length > 0 && (
                            <table className="table table-hover text-start">
                                <thead>
                                    <tr>
                                        <th scope="col">Request</th>
                                        <th scope="col">Cost</th>
                                        <th scope="col">Status</th>
                                        <th scope="col">Type</th>
                                        <th scope="col">Paid</th>
                                        <th scope="col">Delete</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {requests.map((translation) => (
                                        <tr
                                            key={translation.range_key}
                                            style={{
                                                cursor: 'pointer',
                                                color: '#000000'
                                            }}
                                        >
                                            <td><a href={`/translations/myrequests/details?partitionKey=${translation.partition_key}&rangeKey=${translation.range_key}`}>{translation.title}</a></td>
                                            <td>
                                                {translation.estimated_cost}
                                            </td>
                                            <td>
                                                {translation.status}
                                            </td>
                                            <td>
                                                {translation.type}
                                            </td>
                                            <td>
                                                {translation.payment_id.length === 0 ? (
                                                    <Button onClick={() => handlePayRequest(translation.range_key)}>Pay</Button>
                                                ) : (
                                                    <b>Paid: {translation.payment_id}</b>
                                                )}
                                            </td>
                                            <td><Button onClick={() => handleDeleteClicked(translation)}>Delete</Button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </main>
                </div>
            </div>
        </div>

    );
};

export default TranslationsMyRequestsScreen;
